import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import DateField from "../../../DesignSystem/Inputs/DateField";
import InputField from "../../../DesignSystem/Inputs/InputField";
import TextArea from "../../../DesignSystem/Inputs/TextArea";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import FileUpload from "../../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../../DesignSystem/Upload/UploadedFileList";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import Banner from "../../../DesignSystem/Banner/Banner";
import WebsiteAuditLogs from "../components/WebsiteAuditLogs";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import { auth } from "../../../../backend/firebaseConfig";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { createEmailLicence, subscribeEmailLicence, subscribeEmailLicenceAuditLogs, updateEmailLicence } from "../services/emailLicenceService";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotionsSubmission.css";
import "../../../../css/Marketing/WebsiteCharges.css";

import { LICENCE_TYPES } from "../constants/emailLicenceTypes";
const now = new Date();
const today = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-");

const EmailLicenceEntryPage = () => {
  const navigate = useNavigate();
  const { licenceId } = useParams();
  const editing = Boolean(licenceId);
  const { userPermissions } = useUserPermissions();
  const canViewAuditLogs = Array.isArray(userPermissions) && userPermissions.some((permission) => String(permission || "").trim().toLowerCase().includes("administrator"));
  const returnPath = `${APP_ROUTES.MARKETING_WEBSITES}?tab=licences`;
  const { advisers, loadingAdvisers } = useAdviserListWithUnappointed();
  const [formData, setFormData] = useState({ orderDate: today, chargeFromDate: today, adviserName: "", adviserEmail: "", licenceType: "", numberOfLicences: "1", emailAddress: "", domainLinked: "", monthlyAmount: "", notes: "" });
  const [invoice, setInvoice] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(editing && canViewAuditLogs);
  const adviserOptions = useMemo(() => advisers.map((adviser) => {
    const adviserName = [adviser.firstName, adviser.lastName].filter(Boolean).join(" ").trim() || adviser.name || "";
    return { id: adviser.email, name: `${adviser.email}${adviserName ? ` (${adviserName})` : ""}` };
  }), [advisers]);

  useEffect(() => {
    if (!licenceId) return undefined;
    return subscribeEmailLicence(licenceId, (entry) => {
      if (!entry) return navigate(returnPath, { replace: true });
      setFormData((current) => ({
        ...current,
        ...entry,
        chargeFromDate:
          String(entry.chargeFromDate || "").trim() ||
          String(entry.orderDate || "").trim() ||
          current.chargeFromDate,
      }));
      setInvoice(entry.invoice || null);
      setLoading(false);
    }, () => { setSaveError("We couldn't load this email licence."); setLoading(false); });
  }, [licenceId, navigate, returnPath]);

  useEffect(() => {
    if (!licenceId || !canViewAuditLogs) { setAuditLoading(false); return undefined; }
    return subscribeEmailLicenceAuditLogs(licenceId, (logs) => { setAuditLogs(logs); setAuditLoading(false); }, () => setAuditLoading(false));
  }, [licenceId, canViewAuditLogs]);

  const monthlyAmount = (licenceType, numberOfLicences) => {
    const rate = LICENCE_TYPES.find((type) => type.id === licenceType)?.monthlyRate || 0;
    const count = Number(numberOfLicences);
    return rate && Number.isInteger(count) && count > 0 ? (rate * count).toFixed(2) : "";
  };
  const isValid = (field, value) => {
    if (field === "emailAddress") {
      const addresses = String(value || "").split(",").map((address) => address.trim());
      return addresses.length > 0 && addresses.every((address) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address));
    }
    if (field === "domainLinked") {
      if (!String(value || "").trim()) return true;
      const domains = String(value).split(",").map((domain) => domain.trim());
      const domainPattern = /^(?:https?:\/\/)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?(?:\/[^\s]*)?$/i;
      return domains.length > 0 && domains.every((domain) => domainPattern.test(domain));
    }
    if (["orderDate", "adviserName", "licenceType"].includes(field)) return Boolean(String(value || "").trim());
    if (field === "numberOfLicences") return /^[1-9]\d*$/.test(String(value));
    if (field === "monthlyAmount") return /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(String(value));
    return true;
  };
  const update = (field) => (event) => {
    const value = event.target.value;
    setFormData((current) => {
      const next = { ...current, [field]: value };
      if (
        field === "orderDate" &&
        (!current.chargeFromDate || current.chargeFromDate === current.orderDate)
      ) {
        next.chargeFromDate = value;
      }
      if (["licenceType", "numberOfLicences"].includes(field)) next.monthlyAmount = monthlyAmount(next.licenceType, next.numberOfLicences);
      return next;
    });
    setErrors((current) => ({ ...current, [field]: !isValid(field, value) }));
  };
  const updateAdviser = (event) => {
    const adviserEmail = event.target.value;
    const adviser = advisers.find((item) => String(item.email || "").trim().toLowerCase() === String(adviserEmail).trim().toLowerCase());
    const adviserName = [adviser?.firstName, adviser?.lastName].filter(Boolean).join(" ").trim() || adviser?.name || "";
    setFormData((current) => ({ ...current, adviserEmail, adviserName }));
    setErrors((current) => ({ ...current, adviserName: !adviserName }));
  };
  const validate = () => {
    const next = {};
    ["orderDate", "adviserName", "licenceType", "numberOfLicences", "emailAddress", "domainLinked", "monthlyAmount"].forEach((field) => { if (!isValid(field, formData[field])) next[field] = true; });
    setErrors(next);
    return !Object.keys(next).length;
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSaving(true); setSaveError("");
    try {
      const actor = { uid: auth.currentUser?.uid || "", email: auth.currentUser?.email || localStorage.getItem("userEmail") || "", displayName: auth.currentUser?.displayName || "" };
      if (editing) await updateEmailLicence({ licenceId, formData, invoice, actor });
      else await createEmailLicence({ formData, invoice, actor });
      navigate(returnPath, { replace: true, state: { activeTab: "licences", successMessage: editing ? "Email licence updated successfully." : "Email licence saved successfully." } });
    } catch (error) { setSaveError(error.message || "We couldn't save this email licence."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="Main-Page"><Sidebar /><main className="Main-Container website-loading">Loading email licence…</main></div>;
  return <div className="Main-Page">
    <Helmet><title>{editing ? "Email Licence Details" : "Add New Licence Charge"}</title></Helmet><Sidebar />
    <main className="Main-Container fp-submission-container">
      <Breadcrumb items={[{ label: "Marketing", to: APP_ROUTES.DEPARTMENT_MARKETING }, { label: "design⁴", to: returnPath }, { label: editing ? "Email Licence Details" : "Add New Entry" }]} />
       <div className="FP_H3">{editing ? "Email Licence Details" : "Add New Licence Charge"}</div>
        {saveError && <Banner type="error" message={saveError} />}
      <form onSubmit={submit}>
        <section className="fp-form-container fit-column gap-l"><div className="website-fields">
          <DateField label="Date Requested" required value={formData.orderDate} error={errors.orderDate} onChange={update("orderDate")} />
          <DateField label="Charge From Date" value={formData.chargeFromDate} error={errors.chargeFromDate} onChange={update("chargeFromDate")} />
          <CustomSelect label="Adviser Email" options={adviserOptions} value={formData.adviserEmail} placeholder={loadingAdvisers ? "Loading advisers…" : "Select Adviser Email"} disabled={loadingAdvisers} onChange={updateAdviser} clearable />
          <InputField label="Adviser Name" required value={formData.adviserName} placeholder="Adviser Name" error={errors.adviserName} onChange={update("adviserName")} />
          <CustomSelect label="Email Licence Type" required options={LICENCE_TYPES} value={formData.licenceType} placeholder="Choose from options listed" error={errors.licenceType} onChange={update("licenceType")} />
          <InputField label="Number of Licences" required type="number" min="1" step="1" value={formData.numberOfLicences} error={errors.numberOfLicences} onChange={update("numberOfLicences")} />
          <InputField label="Email Address" tooltip="For multiple licences, enter each email address separated by a comma. Every address must be in a valid email format." required type="email" multiple value={formData.emailAddress} error={errors.emailAddress} placeholder="name@example.com, name@example.com" onChange={update("emailAddress")} />
          <InputField label="Domain Linked" tooltip="If multiple domains are linked, enter each domain separated by a comma. Every entry must be in a valid domain format." value={formData.domainLinked} error={errors.domainLinked} placeholder="example.com, example.co.uk" onChange={update("domainLinked")} />
          <InputField label="Monthly Amount to Charge (£)" required type="number" min="0" step="0.01" value={formData.monthlyAmount} error={errors.monthlyAmount} placeholder="Automatically calculated" onChange={update("monthlyAmount")} />
          <div/>
          <div className="website-span-three">
            <TextArea label="Notes" rows={4} value={formData.notes} placeholder="Add any notes for this licence charge" onChange={update("notes")} />
          </div>
          <div className="website-upload-block"><FileUpload label="Invoice" accept=".pdf,.png,.jpg,.jpeg" fileName={invoice?.name} onFilesChange={setInvoice} />{invoice && <UploadedFileList files={[invoice]} onRemove={() => setInvoice(null)} />}</div>
        </div></section>
        <div className="printing-charge-actions row justify-space-between"><SecondaryButton type="button" label="Cancel" onClick={() => navigate(returnPath)} /><PrimaryButton label={saving ? "Saving…" : editing ? "Save Changes" : "Add Entry"} disabled={saving} /></div>
        {editing && canViewAuditLogs && <WebsiteAuditLogs logs={auditLogs} loading={auditLoading} />}
      </form>
    </main>
  </div>;
};

export default EmailLicenceEntryPage;
